import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import styled from 'styled-components';
import Table from 'core/components/HTMLTable';
import { Box, Button, Card, Flex } from 'core/components';
import CodeMirror from 'core/components/CodeMirror';
import MermaidDiagram from 'core/components/MermaidDiagram';
import SvgDiagram from 'core/components/SvgDiagram';
import DeviceConfigView, { isDeviceConfigLanguage } from 'app/components/device/DeviceConfigView';
import CopyToClipboard from 'app/components/CopyToClipboardButton';
import { FiCopy } from 'react-icons/fi';

const boxStyle = { overflowX: 'auto', overflowY: 'hidden' };
const cardStyle = { overflow: 'hidden' };
const tableStyle = { width: '100%', tableLayout: 'auto' };
const codeMirrorOptions = { readOnly: true };

const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [rehypeKatex];

const AnchorComponent = ({ node, href, children, ...props }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

const TableComponent = ({ node, showDownloadCsv = true, ...props }) => {
  const tableRef = React.useRef(null);

  const handleDownload = () => {
    const table = tableRef.current;
    if (!table) {
      return;
    }

    const rows = table.querySelectorAll('tr');
    const csvRows = [];

    // Characters that can trigger CSV injection in Excel/Sheets
    const dangerousChars = ['=', '+', '-', '@', '|', '\t', '\r', '\n'];

    rows.forEach((row) => {
      const cells = row.querySelectorAll('th, td');
      const csvRow = Array.from(cells).map((cell) => {
        // Get text content and trim whitespace (trimming ensures we check the actual first character)
        let text = (cell.textContent || '').trim();

        // Prevent CSV injection: prefix dangerous characters with a single quote
        // This prevents Excel/Sheets from interpreting cells as formulas
        if (text.length > 0 && dangerousChars.includes(text[0])) {
          text = `'${text}`;
        }

        // Escape double quotes by replacing each " with ""
        // Using split/join instead of regex to avoid potential regex bombs
        text = text.split('"').join('""');

        return `"${text}"`;
      });
      csvRows.push(csvRow.join(','));
    });

    // Add BOM (Byte Order Mark) for Excel compatibility
    const BOM = '\uFEFF';
    const csv = BOM + csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'table-data.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {showDownloadCsv && (
        <Flex justifyContent="flex-end" pb="4px">
          <Button icon="download" small minimal text="Download CSV" onClick={handleDownload} />
        </Flex>
      )}
      <Card elevation={1} mb={2} p={0} style={cardStyle}>
        <Box style={boxStyle}>
          <Table ref={tableRef} {...props} style={tableStyle} interactive bordered striped>
            {props.children}
          </Table>
        </Box>
      </Card>
    </>
  );
};

const CodeBlockWrapper = styled.div`
  position: relative;

  .copy-button-wrapper {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
  }
`;

const CodeBlockComponent = ({ children, allowMermaid = true, ...props }) => {
  const textContent = (children?.props?.children || '').trim();
  const className = props.node.children[0]?.properties?.className || [];
  const language = className.length > 0 ? className[0].replace('language-', '') : 'text/plain';

  // Render mermaid diagrams only when allowed (disabled for user-submitted content)
  if (language === 'mermaid' && allowMermaid) {
    return <MermaidDiagram chart={textContent} />;
  }

  // Render SVG diagrams (sanitized before being inserted into the DOM).
  // Claude tends to emit SVG in an ```svg fenced block, but sometimes uses a generic
  // xml/html/untagged block, so we also sniff for an <svg> root in those cases.
  const isGenericMarkupLanguage = ['svg', 'svg+xml', 'xml', 'html', 'xhtml', 'text/plain'].includes(language);
  if (allowMermaid && (language === 'svg' || (isGenericMarkupLanguage && /^<svg[\s>]/i.test(textContent)))) {
    return <SvgDiagram svg={textContent} />;
  }

  // for supported syntax highlighting configs (diff, junos, arista, etc...)
  if (isDeviceConfigLanguage(language)) {
    return <DeviceConfigView configText={textContent} syntax={language} />;
  }

  return (
    <CodeBlockWrapper>
      <div className="copy-button-wrapper bp4-dark">
        <CopyToClipboard text={textContent} tooltip={{ content: 'Copy code', position: 'left' }}>
          <Button small minimal icon={FiCopy} aria-label="Copy code" title="Copy code" />
        </CopyToClipboard>
      </div>
      <CodeMirror mode={language} value={textContent} options={codeMirrorOptions} />
    </CodeBlockWrapper>
  );
};

// align center is the default, but leaving this here in case Dave changes his mind.
export const MarkdownWrap = styled.div`
  font-size: 14px;

  &.smaller {
    font-size: 12px;
  }

  .katex-display > .katex {
    text-align: center;
  }

  .CodeMirror-wrap {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .cm-editor {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .mermaid {
    display: flex;
    justify-content: center;
    margin: 16px 0;

    svg {
      max-width: 100%;
      height: auto;
    }
  }

  line-height: 1.7 !important;

  strong {
    font-weight: 700;
  }

  code {
    font-family: ${({ theme }) => theme.fonts.monospace};
    background: rgba(0, 0, 0, 0.05);
    font-size: 13px;
    font-weight: 500;
    padding: 2px 4px;
    border-radius: 4px;
  }

  .bp4-dark & code {
    background: rgba(255, 255, 255, 0.1);
  }

  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol,
  ul {
    margin-bottom: 16px;
    padding-left: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    li {
      margin-bottom: 8px;

      ul {
        margin-top: 6px;
        margin-bottom: 6px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  blockquote {
    font-size: 11px;
    margin: 8px 0;
    padding: 2px 12px;
    border-left: 2px solid ${({ theme }) => theme.borderColors.thin};
  }

  hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 2rem 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 700;
  }

  h1 {
    font-size: 20px;
  }

  h2 {
    font-size: 18px;
  }

  h3 {
    font-size: 16px;
  }

  h4,
  h5 {
    font-size: 14px;
  }

  h6 {
    font-size: 12px;
  }
`;

class Markdown extends React.Component {
  constructor(props) {
    super(props);
    this.renderTableComponent = this.renderTableComponent.bind(this);
    this.renderCodeBlockComponent = this.renderCodeBlockComponent.bind(this);
  }

  renderTableComponent(props) {
    const { hideCsvDownloadButton = false } = this.props;
    return <TableComponent {...props} showDownloadCsv={!hideCsvDownloadButton} />;
  }

  renderCodeBlockComponent(props) {
    const { allowMermaid = true } = this.props;
    return <CodeBlockComponent {...props} allowMermaid={allowMermaid} />;
  }

  render() {
    const { className, children, skipHtml = true } = this.props;
    const markdownComponents = {
      a: AnchorComponent,
      table: this.renderTableComponent,
      pre: this.renderCodeBlockComponent
    };
    return (
      <MarkdownWrap className={className}>
        <ReactMarkdown
          components={markdownComponents}
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          skipHtml={skipHtml}
        >
          {children}
        </ReactMarkdown>
      </MarkdownWrap>
    );
  }
}

export default Markdown;
